import { useState, useEffect } from 'react'
import {
  Fuel, Building2, Users, BarChart3, CreditCard, Check, Copy,
  Plus, Trash2, ArrowRight, ArrowLeft, Loader,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { lopsidedProducts } from '@petrotrack/shared'
import { useOrgData } from '../hooks/useOrgData'
import { useUsers } from '../hooks/useUsers'
import { FUEL_COLORS } from '../utils/formatters'

const FUEL_TYPES = ['PMS', 'AGO', 'DPK', 'LPG']
const STEPS = ['Welcome', 'Your branch', 'Tanks & pumps', 'Your team', 'All set']
const cell = 'w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none'

// ─── Welcome step ──────────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  { icon: Fuel, title: 'Record daily meter readings', body: 'Open and close each pump every day — PetroTrack works out the litres sold and the naira earned for you.' },
  { icon: CreditCard, title: 'Manage credit sales', body: 'Log credit fuel to transport companies and drivers, then reconcile and export weekly batches.' },
  { icon: BarChart3, title: 'See everything at a glance', body: 'Live dashboards show sales, revenue and stock across every branch you run.' },
]

function WelcomeStep({ name }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Welcome to PetroTrack{name ? `, ${name.split(' ')[0]}` : ''} 👋</h1>
        <p className="text-sm text-gray-500 mt-2">Let's get your station set up. It takes about 2 minutes — here's what PetroTrack does for you:</p>
      </div>
      <div className="space-y-3">
        {HOW_IT_WORKS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="flex gap-3 rounded-xl border border-gray-200 bg-white p-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Branch details step ────────────────────────────────────────────────────────

function BranchStep({ branchName, setBranchName, branchLocation, setBranchLocation, loadingBranch }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Name your first branch</h1>
        <p className="text-sm text-gray-500 mt-2">We created a branch for you at signup. Give it a real name and location — you can add more branches later.</p>
      </div>
      {loadingBranch ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader className="w-4 h-4 animate-spin" /> Loading your branch…</div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">Branch name</label>
            <input
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              placeholder="e.g. Ikeja Main Station"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">Location</label>
            <input
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={branchLocation}
              onChange={(e) => setBranchLocation(e.target.value)}
              placeholder="e.g. Lagos"
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tanks & pumps step ─────────────────────────────────────────────────────────

function TanksPumpsStep({ tanks, setTanks, pumps, setPumps }) {
  const updTank = (i, field, value) => setTanks((prev) => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t))
  const updPump = (i, field, value) => setPumps((prev) => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Add your tanks & pumps</h1>
        <p className="text-sm text-gray-500 mt-2">Tell us what fuel this branch stores and sells. Each product needs at least one tank and one pump.</p>
      </div>

      {/* Tanks */}
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-1 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Tanks <span className="text-xs font-normal text-gray-400">({tanks.length})</span></h3>
          <button onClick={() => setTanks([...tanks, { product: 'PMS', tankNumber: tanks.length + 1, capacity: 50000 }])} className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg"><Plus className="w-3 h-3" /> Add tank</button>
        </div>
        {tanks.length === 0 && <p className="text-xs text-gray-400">No tanks yet. Add one to start tracking stock.</p>}
        <div className="space-y-2">
          {tanks.map((tank, i) => {
            const c = FUEL_COLORS[tank.product] || FUEL_COLORS.PMS
            return (
              <div key={i} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{tank.product}</span>
                <select className={cell} value={tank.product} onChange={(e) => updTank(i, 'product', e.target.value)}>{FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                <input className={cell} type="number" value={tank.tankNumber} onChange={(e) => updTank(i, 'tankNumber', e.target.value)} placeholder="Tank #" />
                <input className={cell} type="number" value={tank.capacity} onChange={(e) => updTank(i, 'capacity', e.target.value)} placeholder="Capacity (L)" />
                <button onClick={() => setTanks(tanks.filter((_, idx) => idx !== i))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Pumps */}
      <div className="space-y-3">
        <div className="flex items-center justify-between pb-1 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Pumps <span className="text-xs font-normal text-gray-400">({pumps.length})</span></h3>
          <button onClick={() => setPumps([...pumps, { product: 'PMS', pumpNumber: pumps.length + 1, name: '', defaultExcessPer20L: 1.0 }])} className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg"><Plus className="w-3 h-3" /> Add pump</button>
        </div>
        <p className="text-xs text-gray-400">Pumps draw from any tank of their product.</p>
        <div className="space-y-2">
          {pumps.map((pump, i) => {
            const c = FUEL_COLORS[pump.product] || FUEL_COLORS.PMS
            return (
              <div key={i} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{pump.product}</span>
                <select className={cell} value={pump.product} onChange={(e) => updPump(i, 'product', e.target.value)}>{FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                <input className={cell} type="number" value={pump.pumpNumber} onChange={(e) => updPump(i, 'pumpNumber', e.target.value)} placeholder="Pump #" />
                <input className={cell} value={pump.name} onChange={(e) => updPump(i, 'name', e.target.value)} placeholder="Name (optional)" />
                <button onClick={() => setPumps(pumps.filter((_, idx) => idx !== i))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Team step ──────────────────────────────────────────────────────────────────

function TeamStep({ branchName, onAddMember, members }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('manager')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleAdd() {
    setError('')
    if (!name.trim()) return setError('Name is required.')
    if (!email.trim()) return setError('Email is required.')
    setSaving(true)
    const username = name.trim().toLowerCase().replace(/\s+/g, '').slice(0, 20)
    const result = await onAddMember({ name: name.trim(), username, email: email.trim(), role })
    setSaving(false)
    if (!result.success) return setError(result.message || 'Could not add this member.')
    setName(''); setEmail(''); setRole('manager')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Add your team</h1>
        <p className="text-sm text-gray-500 mt-2">Invite the managers and staff who'll record readings at <span className="font-medium text-gray-700">{branchName}</span>. Optional — you can do this anytime.</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {['manager', 'staff'].map((r) => (
            <button key={r} type="button" onClick={() => setRole(r)}
              className={`py-2.5 rounded-xl text-sm font-semibold border transition ${role === r ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
              {r === 'manager' ? 'Manager' : 'Staff'}
            </button>
          ))}
        </div>
        <input className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
        <input className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}
        <button onClick={handleAdd} disabled={saving} className="w-full py-2.5 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <><Loader className="w-4 h-4 animate-spin" /> Adding…</> : <><Plus className="w-4 h-4" /> Add member</>}
        </button>
      </div>

      {members.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Added — share these logins</p>
          {members.map((m, i) => (
            <div key={i} className="rounded-xl border border-green-200 bg-green-50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-green-800">{m.name} <span className="font-normal text-green-600">· {m.role}</span></p>
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <CredChip label="Username" value={m.username} />
                <CredChip label="Temp password" value={m.tempPassword} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CredChip({ label, value }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="flex items-center justify-between bg-white border border-green-200 rounded-lg px-2.5 py-1.5 text-left"
    >
      <span><span className="block text-[10px] text-gray-400">{label}</span><span className="font-mono font-semibold text-gray-800">{value}</span></span>
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
    </button>
  )
}

// ─── Done step ──────────────────────────────────────────────────────────────────

function DoneStep({ branchName, tankCount, pumpCount, memberCount }) {
  return (
    <div className="space-y-6 text-center py-4">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
        <Check className="w-8 h-8 text-green-600" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-gray-800">You're all set!</h1>
        <p className="text-sm text-gray-500 mt-2">Your station is ready. Here's what we set up:</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 text-left">
        <Summary icon={Building2} label="Branch" value={branchName} />
        <Summary icon={Fuel} label="Tanks & pumps" value={`${tankCount} tank${tankCount === 1 ? '' : 's'} · ${pumpCount} pump${pumpCount === 1 ? '' : 's'}`} />
        <Summary icon={Users} label="Team members" value={memberCount === 0 ? 'None yet' : `${memberCount} added`} />
      </div>
      <p className="text-xs text-gray-400">You can change any of this anytime from the Manage tab.</p>
    </div>
  )
}

function Summary({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Icon className="w-4.5 h-4.5 text-blue-600" /></div>
      <div className="flex-1"><p className="text-xs text-gray-400">{label}</p><p className="text-sm font-semibold text-gray-800">{value}</p></div>
    </div>
  )
}

// ─── Wizard root ────────────────────────────────────────────────────────────────

export default function OnboardingWizard({ currentUser, onComplete }) {
  const { branches, updateBranch } = useOrgData()
  const { addUser } = useUsers(currentUser)

  const [step, setStep] = useState(0)
  const [branch, setBranch] = useState(null)
  const [branchName, setBranchName] = useState('')
  const [branchLocation, setBranchLocation] = useState('')
  const [tanks, setTanks] = useState([])
  const [pumps, setPumps] = useState([])
  const [members, setMembers] = useState([])
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [stepError, setStepError] = useState('')

  // Prefill from the branch auto-created during signup (first one in this org).
  useEffect(() => {
    if (branch) return
    const own = branches.find((b) => b.organizationId === currentUser.organizationId)
    if (own) {
      setBranch(own)
      setBranchName(own.name || '')
      setBranchLocation(own.location || '')
      setTanks((own.tanks || []).map((t) => ({ product: t.product, tankNumber: t.tankNumber, capacity: t.capacity })))
      setPumps((own.pumps || []).map((p) => ({ product: p.product, pumpNumber: p.pumpNumber, name: p.name || '', defaultExcessPer20L: p.defaultExcessPer20L ?? 1.0 })))
    }
  }, [branches, branch, currentUser.organizationId])

  async function persistBranch() {
    if (!branch) return
    setSaving(true)
    await updateBranch(branch.id, {
      organizationId: branch.organizationId,
      name: branchName.trim() || branch.name,
      location: branchLocation,
      tanks: tanks.map((t) => ({ product: t.product, tankNumber: Number(t.tankNumber), capacity: Number(t.capacity) })),
      pumps: pumps.map((p) => ({ product: p.product, pumpNumber: Number(p.pumpNumber) || undefined, name: p.name, defaultExcessPer20L: Math.max(0, Number(p.defaultExcessPer20L) || 0) })),
    })
    setSaving(false)
  }

  async function addMember({ name, username, email, role }) {
    const result = await addUser({ name, username, email, role, organizationId: currentUser.organizationId, branchId: branch?.id })
    if (result.success) {
      setMembers((prev) => [...prev, { name, username: result.user.username, role, tempPassword: result.tempPassword }])
    }
    return result
  }

  async function finish() {
    setFinishing(true)
    const { error } = await supabase.rpc('complete_onboarding')
    if (error) console.error('[onboarding] complete_onboarding failed:', error.message)
    await onComplete()
    // onComplete refetches the profile; once onboarding_completed flips true the
    // wizard unmounts. If the RPC failed we still proceed so the user isn't stuck.
    setFinishing(false)
  }

  async function handleNext() {
    setStepError('')
    // The tanks & pumps step is the whole point of setup: without at least one
    // tank AND one pump per product, daily entries are impossible and the app
    // looks broken. Block Continue until the kit is complete, then persist.
    if (step === 2) {
      if (tanks.length === 0) return setStepError('Add at least one tank — daily entries need a tank to dip.')
      if (pumps.length === 0) return setStepError('Add at least one pump — daily entries need a pump meter to read.')
      const lopsided = lopsidedProducts({ tanks, pumps })
      if (lopsided.length > 0) {
        return setStepError(lopsided.map((r) => `${r.product} needs ${r.missing.join(' and ')}`).join('; ') + '.')
      }
      await persistBranch()
    }
    if (step < STEPS.length - 1) setStep((s) => s + 1)
    else await finish()
  }

  const pct = Math.round((step / (STEPS.length - 1)) * 100)
  const primaryLabel = step === STEPS.length - 1 ? 'Go to dashboard' : step === 0 ? 'Get started' : 'Continue'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-8 sm:py-12">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-4">Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>
          {step === 0 && <WelcomeStep name={currentUser.fullName || currentUser.name} />}
          {step === 1 && <BranchStep branchName={branchName} setBranchName={setBranchName} branchLocation={branchLocation} setBranchLocation={setBranchLocation} loadingBranch={!branch} />}
          {step === 2 && <TanksPumpsStep tanks={tanks} setTanks={setTanks} pumps={pumps} setPumps={setPumps} />}
          {step === 3 && <TeamStep branchName={branchName || 'your branch'} onAddMember={addMember} members={members} />}
          {step === 4 && <DoneStep branchName={branchName} tankCount={tanks.length} pumpCount={pumps.length} memberCount={members.length} />}
        </div>
      </div>

      {/* Footer actions */}
      <div className="border-t border-gray-200 bg-white px-4 py-4">
        {stepError && (
          <div className="max-w-xl mx-auto mb-3">
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{stepError}</p>
          </div>
        )}
        <div className="max-w-xl mx-auto flex items-center gap-3">
          {step > 0 && step < STEPS.length - 1 && (
            <button onClick={() => { setStepError(''); setStep((s) => s - 1) }} className="px-4 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={saving || finishing}
            className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving || finishing ? <Loader className="w-4 h-4 animate-spin" /> : null}
            {primaryLabel}
            {!saving && !finishing && step < STEPS.length - 1 && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
        {/* No skip: completing setup without tanks & pumps leaves the app unable
            to record daily entries — the one thing it exists to do. */}
      </div>
    </div>
  )
}
